// Sidebar.tsx
import clsx from 'clsx';
import { enqueueSnackbar } from 'notistack';
import { useRef, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  IconLogout,
  IconLoader2,
  IconChevronDown,
  IconChevronRight,
  IconLayoutSidebarLeftExpand,
  IconLayoutSidebarLeftCollapse,
} from '@tabler/icons-react';

import { AvatarGroupList } from '@/components/Avatar';
import CustomImage from '@/components/CustomImage';
import { sidebarMenu } from '@/constants/menuData';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import useWindowSize from '@/hooks/useWindowSize';
import { logoutApi } from '@/service/authService';
import { useSettingsStore } from '@/store/useSettingsStore';
import useUser from '@/store/useUser';
import { generateOrGetUserId } from '@/utils';

const AppSidebar = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { collapsed, setCollapsed } = usePageTitle();
  const { width } = useWindowSize();
  const { user } = useUser();
  const { settings } = useSettingsStore();
  const brandLogoSrc =
    settings?.language === 'ar'
      ? '/assets/images/tirtir-world-ar.png'
      : '/assets/images/tirtir-world-en.png';
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const [openSubmenu, setOpenSubmenu] = useState<number | string | null>(null);
  const [hoveredMenu, setHoveredMenu] = useState<number | string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { formatText } = useFormatText();

  // Open submenu if any child is active on initial render
  useEffect(() => {
    const menuWithActiveChild = sidebarMenu?.find(
      (item) =>
        item?.children && item?.children?.some((child) => isActive(child?.link))
    );
    if (menuWithActiveChild) {
      setOpenSubmenu(menuWithActiveChild?.id);
    } else {
      setOpenSubmenu(null);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open submenu if any child is active on initial render
  useEffect(() => {
    const menuWithActiveChild = sidebarMenu?.find(
      (item) =>
        item?.children && item?.children?.some((child) => isActive(child?.link))
    );
    if (menuWithActiveChild) {
      setOpenSubmenu(menuWithActiveChild?.id);
    } else {
      setOpenSubmenu(null);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSubmenu = (id: number | string) => {
    setOpenSubmenu((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    if (width < 950) {
      setCollapsed(true);
    }
  }, [width, setCollapsed]);

  // Close popup on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
    }
    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileMenuOpen]);

  const { hasAccess, isFullAccess } = useAccess();

  const filteredMenu = sidebarMenu.filter((item) => {
    if (item?.onlyForAdmin && !isFullAccess) return false;

    if (isFullAccess) return true;

    if (item.sectionName) {
      return hasAccess(item.sectionName);
    }

    return true;
  });

  const isActive = (path: string) => {
    if (path === '/') {
      return currentPath === '/';
    }
    if (
      path.includes('/user-management/children') &&
      currentPath.includes('/user-management/children')
    ) {
      return true;
    }
    if (
      path.includes('/report-analytics/') &&
      currentPath.includes('/report-analytics/')
    ) {
      return true;
    }
    return currentPath.startsWith(path);
  };

  useEffect(() => {
    const activeParent = filteredMenu?.find((item) =>
      item?.children?.some((child) => child?.link && isActive(child?.link))
    );

    if (activeParent?.id) {
      setOpenSubmenu(activeParent.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    setIsLoading(true);

    const deviceId = generateOrGetUserId();
    const response = await logoutApi(deviceId);
    if (response?.status) {
      enqueueSnackbar(response?.message, { variant: 'success' });
      localStorage.clear();
      // localStorage.removeItem('supporting-properties');
      window.location.reload();
    } else {
      setIsLoading(false);
      console.error(response?.message);
    }
  };

  return (
    <div
      className={clsx(
        'h-screen p-2 sm:p-3 flex flex-col items-start gap-6 bg-neutral-light-50 transition-all duration-200',
        collapsed ? '' : 'max-w-70 w-full'
      )}
    >
      <div
        className={clsx(
          'flex items-center justify-between w-full transition-all delay-150 duration-150',
          collapsed && 'justify-center'
        )}
      >
        {!collapsed && (
          <Link
            to="/"
            className={clsx(
              'flex items-center',
              collapsed && 'justify-center w-full'
            )}
          >
            <CustomImage
              src={brandLogoSrc}
              alt={formatText('auth-layout.brand-alt')}
              className="h-12 w-auto select-none"
            />
          </Link>
        )}

        <div
          className={clsx(
            'py-2.5 px-1 rounded-lg justify-start cursor-pointer items-center gap-2 inline-flex  hover:bg-neutral-100',
            collapsed && 'mx-auto'
          )}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <IconLayoutSidebarLeftExpand
              stroke={1.5}
              size={24}
              color="#717784"
              className="hover:text-neutral-800 rtl:rotate-180"
            />
          ) : (
            <IconLayoutSidebarLeftCollapse
              stroke={1.5}
              size={24}
              color="#717784"
              className="hover:text-neutral-800 rtl:rotate-180"
            />
          )}
        </div>
      </div>
      <div
        className={clsx(
          'inline-flex h-full flex-col items-start gap-1 w-full flex-1',
          collapsed && 'items-center'
        )}
      >
        {filteredMenu.map((item) => {
          if (item?.children) {
            const isMenuOpen = openSubmenu === item.id;
            const isChildActive = item.children.some((child) =>
              isActive(child.link)
            );

            const hasChildren = Boolean(item?.children);
            const isHovered = collapsed && hoveredMenu === item.id;

            return (
              <div
                key={item.id}
                className="flex flex-col w-full gap-1 relative group"
                onMouseEnter={() => collapsed && setHoveredMenu(item.id)}
                onMouseLeave={() => collapsed && setHoveredMenu(null)}
              >
                <div
                  className={clsx(
                    'flex text-neutral-secondary p-2.5 gap-2 w-full items-center rounded-lg cursor-pointer transition-all delay-150 duration-150',
                    isChildActive
                      ? 'bg-brand-default text-white shadow-sharp'
                      : 'hover:bg-brand-hover hover:text-white group-hover:bg-brand-hover group-hover:text-white',
                    collapsed && 'justify-center px-2.5'
                  )}
                  onClick={() => {
                    if (collapsed) {
                      setCollapsed(false);
                      setTimeout(() => {
                        toggleSubmenu(item.id);
                      }, 100);
                    } else {
                      toggleSubmenu(item.id);
                    }
                  }}
                >
                  {item.icon ? (
                    <item.icon
                      stroke={1.5}
                      className={clsx(
                        isChildActive && 'text-white',
                        collapsed && 'mx-auto'
                      )}
                      size={20}
                    />
                  ) : (
                    ''
                  )}
                  {!collapsed && (
                    <>
                      <p
                        className={clsx(
                          'text-sm font-medium flex-1',
                          isChildActive && 'text-white'
                        )}
                      >
                        {formatText(item.name)}
                      </p>
                      {isMenuOpen ? (
                        <IconChevronDown size={18} />
                      ) : (
                        <IconChevronRight
                          size={18}
                          className="rtl:rotate-180"
                        />
                      )}
                    </>
                  )}
                </div>
                {!collapsed && isMenuOpen && (
                  <div className="flex flex-col gap-1 w-full relative">
                    <div className="absolute left-5.5 rtl:left-auto rtl:right-5.5 top-0 bottom-2 w-px bg-neutral-200"></div>

                    {item?.children?.map((child) => (
                      <Link
                        to={child?.disabled ? '#' : child?.link || ''}
                        onClick={(e) => child?.disabled && e.preventDefault()}
                        key={child?.id}
                        className={clsx(
                          'flex text-neutral-secondary py-2 pe-2 ps-10.5 gap-2 w-full items-center rounded-e-lg cursor-pointer transition-all duration-150 relative',
                          isActive(child?.link || '')
                            ? 'text-neutral-primary font-semibold'
                            : 'hover:text-neutral-900',
                          child?.disabled && 'cursor-not-allowed!'
                        )}
                      >
                        {child.icon && <child.icon size={18} stroke={1.5} />}
                        <span className="text-sm">
                          {formatText(child?.name)}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
                {isHovered && (
                  <div
                    className="absolute top-0 ml-0.5 z-50 left-full rtl:right-full rtl:left-auto rtl:ml-0 rtl:mr-0.5"
                    onMouseEnter={() => setHoveredMenu(item.id)}
                    onMouseLeave={() => setHoveredMenu(null)}
                  >
                    <div className="bg-white rounded-xl shadow-lg min-w-55 p-2">
                      <p className="px-3 py-2 text-sm font-semibold text-neutral-primary">
                        {formatText(item.name)}
                      </p>

                      {hasChildren && (
                        <div className="flex flex-col gap-1">
                          {item.children!.map((child) => (
                            <Link
                              key={child.id}
                              to={child.disabled ? '#' : child.link}
                              className={clsx(
                                'px-3 py-2 rounded-lg text-sm',
                                isActive(child.link)
                                  ? 'bg-brand-light text-brand-default font-semibold'
                                  : 'text-neutral-secondary hover:bg-neutral-100',
                                child.disabled && 'cursor-not-allowed!'
                              )}
                            >
                              {formatText(child?.name)}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          return (
            <div
              key={item.id}
              className="relative w-full"
              onMouseEnter={() => collapsed && setHoveredMenu(item.id)}
              onMouseLeave={() => collapsed && setHoveredMenu(null)}
            >
              <Link
                to={item?.disabled ? '#' : item?.link || ''}
                onClick={(e) => item?.disabled && e.preventDefault()}
                className={clsx(
                  'flex text-neutral-secondary hover:text-white p-2.5 gap-2 w-full items-center rounded-lg cursor-pointer aria-disabled:cursor-not-allowed transition-all delay-150 duration-150',
                  isActive(item?.link || '')
                    ? 'bg-brand-default shadow-sharp'
                    : 'hover:bg-brand-hover',
                  collapsed && 'justify-center px-2.5',
                  item?.hidden && 'hidden',
                  item?.disabled && 'cursor-not-allowed!'
                )}
              >
                {item.icon && (
                  <item.icon
                    stroke={1.5}
                    className={clsx(
                      isActive(item?.link || '') && 'text-white',
                      collapsed && 'mx-auto'
                    )}
                    size={20}
                  />
                )}

                {!collapsed && (
                  <p
                    className={clsx(
                      'text-sm font-medium',
                      isActive(item?.link || '') && 'text-white'
                    )}
                  >
                    {formatText(item?.name)}
                  </p>
                )}
              </Link>

              {collapsed && hoveredMenu === item?.id && (
                <div className="absolute rtl:right-full rtl:top-1/2 rtl:translate-y--1/2 rtl:mr-2 left-full top-1/2 -translate-y-1/2 ml-2 z-50 pointer-events-none w-fit">
                  <div className="bg-white text-xs text-neutral-primary font-medium px-3 py-1.5 rounded-md shadow-lg whitespace-nowrap">
                    {formatText(item.name)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div
        className={clsx(
          'flex p-2 flex-col items-start gap-2 w-full rounded-lg border border-neutral-100 bg-white shadow-customInset relative bottom-8',
          collapsed && 'items-center justify-center p-2'
        )}
        ref={profileMenuRef}
      >
        <div
          className={clsx(
            'flex items-center gap-2 w-full relative',
            collapsed ? 'justify-center' : 'justify-between'
          )}
        >
          <div
            className={clsx(
              'flex items-center gap-2 w-full cursor-auto',
              collapsed && 'justify-center '
            )}
            onClick={() => {
              if (collapsed) setProfileMenuOpen((prev) => !prev);
            }}
          >
            <AvatarGroupList
              list={[
                {
                  id: user?.id,
                  name: user?.name,
                  image: user?.profile_image,
                },
              ]}
              size={40}
              className="w-10! h-10! min-w-10! rounded-full object-cover"
            />

            {!collapsed && (
              <div className="inline-flex flex-col justify-start items-start">
                <p className="text-sm font-semibold text-neutral-primary capitalize">
                  {user?.name}
                </p>
                <p className="text-sm font-light text-neutral-secondary truncate max-w-40">
                  {user?.email}
                </p>
              </div>
            )}

            {!collapsed && (
              <span
                className="text-neutral-primary font-medium  ms-auto shrink-0 cursor-pointer"
                onClick={handleLogout}
              >
                {isLoading ? (
                  <IconLoader2 className="animate-spin h-5 w-5" />
                ) : (
                  <IconLogout size={24} className="rtl:rotate-180" />
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppSidebar;
